/**
 * useIntersectionObserver - Reusable IntersectionObserver hook
 * 
 * Provides a clean interface for using IntersectionObserver API
 * with automatic cleanup and SSR safety.
 * 
 * Use cases:
 * - Lazy loading images/components
 * - Infinite scroll triggers
 * - Viewport visibility detection
 * 
 * @module hooks/useIntersectionObserver
 */
"use client";

import { useEffect, useRef, useState, useCallback, type RefObject } from "react";

// ============================================================================
// Types
// ============================================================================

export interface UseIntersectionObserverOptions {
  /** Root element for intersection (default: viewport) */
  readonly root?: Element | null;
  /** Margin around root (default: "0px") */
  readonly rootMargin?: string;
  /** Visibility threshold(s) 0-1 (default: 0) */
  readonly threshold?: number | number[];
  /** Whether observer is enabled (default: true) */
  readonly enabled?: boolean;
  /** Only trigger once (default: false) */
  readonly triggerOnce?: boolean;
  /** Callback when intersection changes */
  readonly onIntersect?: (entry: IntersectionObserverEntry) => void;
}

export interface UseIntersectionObserverReturn<T extends Element> {
  /** Ref to attach to target element */
  readonly ref: RefObject<T | null>;
  /** Whether element is currently visible */
  readonly isIntersecting: boolean;
  /** Full intersection entry (for advanced use) */
  readonly entry: IntersectionObserverEntry | null;
  /** Manually disconnect observer */
  readonly disconnect: () => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook to observe element intersection with viewport
 * 
 * @example
 * ```tsx
 * // Basic lazy loading
 * const { ref, isIntersecting } = useIntersectionObserver<HTMLDivElement>();
 * 
 * return (
 *   <div ref={ref}>
 *     {isIntersecting && <ExpensiveComponent />}
 *   </div>
 * );
 * ```
 * 
 * @example
 * ```tsx
 * // Infinite scroll trigger
 * const { ref } = useIntersectionObserver<HTMLDivElement>({
 *   rootMargin: "200px", // Trigger 200px before visible
 *   onIntersect: (entry) => {
 *     if (entry.isIntersecting) loadMore();
 *   }
 * });
 * 
 * return <div ref={ref} className="load-more-trigger" />;
 * ```
 */
export function useIntersectionObserver<T extends Element = HTMLDivElement>(
  options: UseIntersectionObserverOptions = {}
): UseIntersectionObserverReturn<T> {
  const {
    root = null,
    rootMargin = "0px",
    threshold = 0,
    enabled = true,
    triggerOnce = false,
    onIntersect
  } = options;

  const ref = useRef<T | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const hasTriggeredRef = useRef(false);

  /**
   * Disconnect observer manually
   */
  const disconnect = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  /**
   * Setup IntersectionObserver
   */
  useEffect(() => {
    // SSR safety
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      return;
    }

    // Skip if disabled
    if (!enabled) {
      disconnect();
      return;
    }

    // Skip if already triggered in triggerOnce mode
    if (triggerOnce && hasTriggeredRef.current) {
      return;
    }

    const element = ref.current;
    if (!element) {
      return;
    }

    // Create observer
    const observer = new IntersectionObserver(
      (entries) => {
        const [firstEntry] = entries;
        if (!firstEntry) return;

        setEntry(firstEntry);
        setIsIntersecting(firstEntry.isIntersecting);

        // Call callback
        onIntersect?.(firstEntry);

        // Handle triggerOnce
        if (triggerOnce && firstEntry.isIntersecting) {
          hasTriggeredRef.current = true;
          observer.disconnect();
        }
      },
      {
        root,
        rootMargin,
        threshold
      }
    );

    observerRef.current = observer;
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [enabled, root, rootMargin, threshold, triggerOnce, onIntersect, disconnect]);

  return {
    ref,
    isIntersecting,
    entry,
    disconnect
  };
}

export default useIntersectionObserver;
